import { Check, Trash2 } from 'lucide-react'

const FIELDS = [
  { key: 'meetingGoals', label: 'Goals' },
  { key: 'timeRules', label: 'Time' },
  { key: 'participantExpectations', label: 'People' },
  { key: 'requiredQuestions', label: 'Questions' },
  { key: 'steeringPriorities', label: 'Focus' },
]

/**
 * Minimal, ambient sidebar for the leader proxy chat.
 * Shows what's been captured — not what's missing.
 * Feels like a notepad, not a checklist.
 */
export default function BriefProgressPanel({ fieldCompleteness, brief, agenda, emailChecks, onDiscard }) {
  if (!fieldCompleteness) return null

  const filledCount = FIELDS.filter(
    (f) => fieldCompleteness[f.key] === 'populated' || fieldCompleteness[f.key] === 'partial'
  ).length

  const hasGoals = Array.isArray(brief?.meetingGoals) && brief.meetingGoals.length > 0
  const hasTime = brief?.timeRules?.meetingHardStopMinutes || brief?.timeRules?.perPersonMaxSeconds
  const hasParticipants = Array.isArray(brief?.participantExpectations) && brief.participantExpectations.length > 0
  const hasQuestions = Array.isArray(brief?.requiredQuestions) && brief.requiredQuestions.length > 0
  const hasSteering = Array.isArray(brief?.steeringPriorities) && brief.steeringPriorities.length > 0
  const agendaItems = Array.isArray(agenda) ? agenda : []
  const hasAgenda = agendaItems.length > 0
  const agendaTotalMin = agendaItems.reduce((s, a) => s + (Number(a?.durationMin) || 0), 0)

  const hasAnything = hasGoals || hasTime || hasParticipants || hasQuestions || hasSteering || hasAgenda

  return (
    <div className="flex h-full flex-col p-4 pt-5">
      {/* Subtle progress dots */}
      <div className="flex items-center gap-1.5 mb-4">
        {FIELDS.map((f) => {
          const status = fieldCompleteness[f.key] || 'empty'
          const filled = status === 'populated' || status === 'partial'
          return (
            <div
              key={f.key}
              className={`h-1 flex-1 rounded-full transition-all duration-700 ${
                filled ? 'bg-primary-500' : 'bg-gray-200'
              }`}
              title={f.label}
            />
          )
        })}
      </div>

      {!hasAnything && (
        <p className="text-xs text-gray-400 leading-relaxed">
          I'll capture your meeting details here as we chat.
        </p>
      )}

      {hasAnything && (
        <div className="space-y-3.5">
          {hasGoals && (
            <CapturedSection label="Goals">
              {brief.meetingGoals.map((g, i) => (
                <CapturedItem key={i} text={g.description} priority={g.priority} />
              ))}
            </CapturedSection>
          )}

          {hasTime && (
            <CapturedSection label="Time">
              <p className="text-[12px] text-gray-600 leading-snug">
                {brief.timeRules.meetingHardStopMinutes ? `${brief.timeRules.meetingHardStopMinutes} min` : ''}
                {brief.timeRules.meetingHardStopMinutes && brief.timeRules.perPersonMaxSeconds ? ' · ' : ''}
                {brief.timeRules.perPersonMaxSeconds ? `${Math.round(brief.timeRules.perPersonMaxSeconds / 60)} min/person` : ''}
              </p>
            </CapturedSection>
          )}

          {hasParticipants && (
            <CapturedSection label="People">
              <ul className="space-y-1">
                {brief.participantExpectations.map((p, i) => {
                  const email = (p.participantEmail ?? '').trim()
                  const isExternal = p.participantKind === 'external'
                  const check = email ? emailChecks?.[email.toLowerCase()] : null
                  const emailInvalid = check && check.valid === false
                  return (
                    <li key={i} className="text-[12px] leading-snug">
                      <span className="text-gray-700">{p.participantName}</span>
                      {isExternal && (
                        <span className="ml-1.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700">
                          external
                        </span>
                      )}
                      <div className={!email ? 'text-[11px] text-amber-600' : emailInvalid ? 'text-[11px] text-red-600' : 'text-[11px] text-gray-400'}>
                        {email || 'no email yet'}
                      </div>
                      {emailInvalid && (
                        <div className="text-[10px] text-red-500">
                          {check.reason === 'invalid_format' ? 'doesn’t look like a valid email' : 'this domain can’t receive mail — check the spelling'}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </CapturedSection>
          )}

          {hasAgenda && (
            <CapturedSection label={`Agenda${agendaTotalMin > 0 ? ` · ${agendaTotalMin} min` : ''}`}>
              <ol className="space-y-1">
                {agendaItems.map((a, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-2 text-[12px] leading-snug">
                    <span className="text-gray-600">
                      <span className="text-gray-400">{i + 1}.</span> {a?.title || 'Untitled item'}
                      {a?.owner ? <span className="text-gray-400"> — {a.owner}</span> : null}
                    </span>
                    {Number(a?.durationMin) > 0 && (
                      <span className="shrink-0 text-[11px] tabular-nums text-gray-400">{a.durationMin}m</span>
                    )}
                  </li>
                ))}
              </ol>
            </CapturedSection>
          )}

          {hasQuestions && (
            <CapturedSection label="Questions">
              {brief.requiredQuestions.map((q, i) => (
                <p key={i} className="text-[12px] text-gray-600 leading-snug">{q.question}</p>
              ))}
            </CapturedSection>
          )}

          {hasSteering && (
            <CapturedSection label="Focus">
              <p className="text-[12px] text-gray-600">
                {brief.steeringPriorities
                  .map((s) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
                  .join(', ')}
              </p>
            </CapturedSection>
          )}
        </div>
      )}

      {filledCount >= 2 && (
        <div className="mt-5 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <Check className="w-3 h-3 text-emerald-500" />
            <span className="text-[11px] text-emerald-600 font-medium">
              {filledCount === FIELDS.length ? 'Brief complete' : `${filledCount} of ${FIELDS.length} covered`}
            </span>
          </div>
        </div>
      )}

      {/* Always-available exit from an in-progress session, pinned to the
          bottom-right of the sidebar: deletes the draft (confirming first if
          there's chat history), clears the saved session, returns to dashboard. */}
      {onDiscard && (
        <div className="mt-auto flex justify-end pt-4">
          <button
            type="button"
            onClick={onDiscard}
            className="inline-flex items-center gap-1.5 text-[12px] text-gray-400 transition-colors hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Discard
          </button>
        </div>
      )}
    </div>
  )
}

function CapturedSection({ label, children }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  )
}

function CapturedItem({ text, priority }) {
  return (
    <div className="flex items-start gap-1.5">
      <span className={`mt-1.5 h-1 w-1 rounded-full shrink-0 ${
        priority === 'must_complete' ? 'bg-primary-500' : 'bg-gray-300'
      }`} />
      <p className="text-[12px] text-gray-600 leading-snug">{text}</p>
    </div>
  )
}
